/**
 * The load-time derivations the old client performs and the database does not
 * carry — applied here, at the read boundary, so every surface sees the same
 * numbers the live app shows.
 *
 * ## 🚨 Why this module exists
 *
 * A side-by-side screenshot of the Trades tab showed the two clients disagreeing
 * on the same trades, three ways:
 *
 * | | live app | new client (before) |
 * |---|---|---|
 * | a 22:36 trade's day | 2026-07-29 | 2026-07-28 |
 * | commissions | +$7.00 | $0.00 |
 * | net P&L | -$132.00 | -$125.00 |
 *
 * None of it was a rendering bug. **The old client re-derives these fields on
 * every load and then trusts its own answer, not the column.** Its own comments
 * say so in as many words:
 *
 *   `session_date` — "Recomputed every load to stay in sync with edits to the raw
 *   timestamp… the safety net that keeps storage and derivation in sync."
 *
 *   `fees` — "Self-heal commissions on load… Fixes synced/imported fee-less
 *   trades that never hit an import or config-save backfill."
 *
 * So the stored columns are a CACHE, and one that is demonstrably stale on real
 * rows in the live database right now. A port that reads them and renders is not
 * simpler — it is wrong, in two directions that both look plausible: trades
 * bucketed onto the previous day, and results flattered by the commissions they
 * never subtracted.
 *
 * ## The derivations
 *
 * 1. **Session date** — always recomputed from `date` + `timeIn` + the user's
 *    session timezone. Never read from `session_date`.
 * 2. **Fees and net P&L** — healed from the account firm's rate schedule, unless
 *    the user typed the fee by hand (`feesAuto === false`).
 * 3. **MAE / MFE floors** — raised to the realized-P&L invariant. Price provably
 *    reached the exit, so a loser's adverse excursion was at least its realized
 *    loss; a stored value below that came from a bar window that missed. The old
 *    client clamps this on every load too (audit item F9), and the failure is
 *    once again flattering: a smaller MAE reads as a tighter, better managed
 *    trade than the one actually taken.
 *
 * The list is not assumed complete. It came from auditing `migrateState` and the
 * tail of `loadStateFromCloud`; anything else recomputed there belongs here.
 *
 * ## What this deliberately does NOT do
 *
 * Persist. The old client writes its healed values back through the save-diff;
 * this returns new rows and leaves the database alone. Writes are deferred in
 * Phase 4, and "rendering a table rewrites the user's trades" is not a side
 * effect worth having even when writes land — that will be its own decision.
 */
import { applyCommission, type CommissionSchedules, type FeeTrade } from '@domain/commissions';
import { excursionFloorClamp } from '@domain/excursion';
import { sessionDateFor } from '@domain/session';
import type { Account } from '@domain/types';

/** The fields a row must carry to be derivable. */
export interface DerivableRow extends FeeTrade {
  accountId: string | null;
  date: string | null;
  timeIn: string | null;
  sessionDate: string | null;
  entry: number | null;
  exit: number | null;
  side: string | null;
  maePoints: number | null;
  mfePoints: number | null;
}

export interface DeriveContext {
  accounts: readonly Account[];
  schedules: CommissionSchedules;
  /** `prefs.sessionTimezone`. Governs which ET hour a clock time maps to. */
  sessionTz: string;
}

/**
 * Apply every load-time derivation to one row.
 *
 * ⚠ Returns a NEW object; the input is never mutated. Rows come from a read and
 * are held in React state, and mutating them in place would leave two renders
 * disagreeing about the same array.
 */
export function deriveRow<T extends DerivableRow>(row: T, ctx: DeriveContext): T {
  // ── 1. the session day ─────────────────────────────────────────────────────
  // ⚠ Recomputed unconditionally, NOT "filled in when null". The stored column
  // is stale rather than absent on the rows that matter — a 22:36 trade whose
  // `session_date` says the same calendar day — so a null-check would leave
  // exactly the wrong rows wrong.
  const sessionDate = row.date
    ? (sessionDateFor(row.date, row.timeIn, ctx.sessionTz) || null)
    : row.sessionDate;

  // ── 2. the excursion floors ────────────────────────────────────────────────
  // Only ever raises, and only a non-null value. See excursionFloorClamp.
  const fl = excursionFloorClamp(row.maePoints, row.mfePoints, row.entry, row.exit, row.side);

  // ── 3. the fee, and the net it implies ─────────────────────────────────────
  const firm = row.accountId
    ? ctx.accounts.find((a) => String(a.id) === String(row.accountId))?.firm
    : null;

  const patched = {
    ...row,
    sessionDate,
    maePoints: fl.maePoints,
    mfePoints: fl.mfePoints,
    // ⚠ Surfaced, not swallowed. A clamped excursion means the bar window was
    // suspect or incomplete, and the Excursion analytics gate on knowing that —
    // the old client carries the same marker for the same reason (F6).
    ...(fl.clamped ? { excursionClamped: true } : {}),
  } as T;

  return applyCommission(patched, firm, ctx.schedules);
}

export function deriveRows<T extends DerivableRow>(
  rows: readonly T[],
  ctx: DeriveContext,
): T[] {
  return rows.map((r) => deriveRow(r, ctx));
}

export type { CommissionSchedules } from '@domain/commissions';
